import {
  InteractionModalAccordionsHook,
  InteractionModalComponentProps,
  ModalValidationRules,
} from '$/components/Interactions/types';
import { StyleProps } from '$/components/ui';
import {
  AttendeeFragment,
  GetRegistrantsOrderBy,
  InteractionDetails,
  InteractionForAttendeeFragment,
  InteractionFragment,
  PublicEWebinarFragment,
  ReactionFragment,
  ResultsAppearAt,
  SelectionType,
} from '$/GraphQL/schema/schema';
import { QuizIcon } from '$/images/FontAwesomeIcons';
import { RenderColumnData } from '$/sections/Home/Dashboard/Registrants/types';
import pluralize from 'pluralize';
import React from 'react';
import { TableCellProps } from 'react-virtualized';
import * as yup from 'yup';
import { AnalyticsCardProps } from '../cards/types';
import { InteractionInfo } from '../InteractionInfo';
import { PollInfo } from '../Poll/PollInfo';
import QuizCard from './QuizCard';
import { useQuizSettings } from './useQuizSettings';
import { QuizInteractionAttendeeResults } from './types';

const QuizModal = React.lazy(() => import('./QuizModal'));
const AnalyticsQuizCardBody = React.lazy(() => import('./AnalyticsQuizCard'));

interface ValidationData {
  isAnswer: boolean;
}
export class QuizInfo extends PollInfo {
  public constructor(
    i: InteractionForAttendeeFragment | InteractionFragment,
    ewebinar?: PublicEWebinarFragment,
    attendee?: AttendeeFragment
  ) {
    super(i, ewebinar, attendee);
  }

  public icon(): React.FC<StyleProps> {
    return QuizIcon;
  }

  public modalValidationRules(): ModalValidationRules {
    return {
      ...super.modalValidationRules(),
      answers: yup
        .array()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .when('publishAsTemplate', ([value]: boolean[], field: any) =>
          value
            ? field.of(
                yup.object({
                  text: yup.string().trim(),
                  isAnswer: yup.boolean().nullable().notRequired(),
                })
              )
            : field.of(
                yup.object({
                  text: yup.string().trim().required(),
                  isAnswer: yup.boolean().nullable().notRequired(),
                })
              )
        )
        .min(
          this.minRange(),
          `You must have at least ${pluralize('answer', this.minRange(), true)}`
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .when('publishAsTemplate', ([value]: boolean[], field: any) =>
          value
            ? field
            : field.required().test({
                name: 'hasCorrectAnswers',
                test: async function (values: ValidationData[]) {
                  const haveAnswers = values.some((value: ValidationData) => value.isAnswer);
                  return haveAnswers
                    ? true
                    : this.createError({
                        message: `Choose the correct answer(s) using the circles below.`,
                        path: 'noAnswerError',
                      });
                },
              })
        ),
    };
  }

  public attendeeCardValidationRules(): yup.AnyObjectSchema | undefined {
    return yup.object().shape({
      arrayAnswer: yup.array(yup.string()).required(),
    });
  }

  public shouldShowResultState(playbackPosition: number): boolean {
    const show = super.shouldShowResultState(playbackPosition);
    if (!show) return show;
    if (
      this.i.details?.resultsAppearAt === ResultsAppearAt.EndOfPresentation &&
      Boolean(this.reaction)
    ) {
      return true;
    }
    return show;
  }

  public useModalPreviewComponent(): React.LazyExoticComponent<
    React.FC<InteractionModalComponentProps>
  > | null {
    return QuizModal;
  }

  public modalSettingsAccordions(): InteractionModalAccordionsHook {
    return useQuizSettings;
  }

  public detailsDefault(interactions: InteractionInfo[]): InteractionDetails {
    const d: InteractionDetails = {
      ...super.detailsDefault(interactions),
      showCumulativeResults: true,
      resultsAppearAt: ResultsAppearAt.Immediately,
    };

    return d;
  }

  public renderColumnData(data: RenderColumnData): RenderColumnData | null {
    return {
      ...data,
      width: this.i.details?.selectionType === SelectionType.Multi ? 240 : 120,
      orderBy: GetRegistrantsOrderBy.PollAnswer,
      cellRenderer: ({ cellData }: TableCellProps): JSX.Element => {
        const reaction = cellData as ReactionFragment;
        if (!reaction?.arrayAnswer) {
          return <div />;
        }
        return (
          <div className='pre-wrap'>
            {reaction.numberAnswer === 1 ? 'Correct' : 'Incorrect'}:{' '}
            {reaction.arrayAnswer.join(', ')}
          </div>
        );
      },
    };
  }

  // Surface Text instead of Value for Chat Message
  public answerMessage(reaction: ReactionFragment): string {
    const msg = super.answerMessage(reaction);
    if (!msg) return msg;
    return `${reaction.numberAnswer === 1 ? 'Correct' : 'Incorrect'}: ${msg}`;
  }

  public results(): QuizInteractionAttendeeResults | undefined | null {
    const results = super.results();
    if (!results) return null;

    type R = Record<string, number>;
    const resultMaps = (results.answers ?? []).reduce<R>((map, a) => {
      map[a.answer] = a.percent;
      return map;
    }, {});

    const answerValues = this.fragment.reaction?.arrayAnswer?.map((a) => a.toLowerCase()) ?? [];
    const answers = this.i.details?.answers?.map((a) => {
      const value = (a.value ? a.value : a.text).toLowerCase();
      return {
        answer: a.text,
        isAnswer: a.isAnswer!,
        percent: resultMaps[value],
        matched: a.isAnswer! && answerValues.includes(value),
        unmatched: answerValues.includes(value) && !a.isAnswer,
      };
    });
    return { answers: answers ?? [] };
  }

  public analyticsCardComponent(): React.LazyExoticComponent<React.FC<AnalyticsCardProps>> {
    return AnalyticsQuizCardBody;
  }

  public cardBodyComponent(): React.FC | React.LazyExoticComponent<React.FC<{}>> {
    return QuizCard;
  }
}
